import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID, provideAppInitializer, inject } from '@angular/core';
import {
    PreloadAllModules,
    provideRouter,
    Router,
    withComponentInputBinding,
    withPreloading,
    withRouterConfig
} from '@angular/router';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@bluehalo/ngx-leaflet-markercluster';
import { routes } from './app.routes';
import { HttpClient, provideHttpClient, withJsonpSupport } from '@angular/common/http';
import { NgIconsModule } from '@ng-icons/core';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import * as Sentry from '@sentry/angular';
import {
    heroAdjustmentsVertical, heroArrowSmallDown,
    heroArrowSmallUp,
    heroArrowsRightLeft,
    heroArrowsUpDown,
    heroArrowTopRightOnSquare,
    heroArrowTrendingUp, heroArrowUpRight,
    heroBars2,
    heroBars3,
    heroBell,
    heroBuildingOffice,
    heroBuildingOffice2,
    heroChevronDoubleLeft,
    heroChevronDoubleUp,
    heroChevronDown,
    heroChevronRight,
    heroChevronUp,
    heroChevronUpDown,
    heroCog,
    heroCreditCard,
    heroCursorArrowRays,
    heroDevicePhoneMobile, heroEllipsisVertical,
    heroEnvelope, heroExclamationTriangle,
    heroEye,
    heroFingerPrint,
    heroGlobeEuropeAfrica, heroInformationCircle,
    heroMagnifyingGlass,
    heroMapPin,
    heroMinus,
    heroPencil,
    heroPhone,
    heroPhoto,
    heroPlus,
    heroShoppingCart,
    heroSpeakerWave,
    heroSquares2x2, heroStar, heroUser,
    heroUserCircle,
    heroUserPlus,
    heroUsers,
    heroXMark
} from '@ng-icons/heroicons/outline';

import {
    heroCalendarSolid,
    heroEnvelopeSolid,
    heroFunnelSolid,
    heroMagnifyingGlassSolid,
    heroMapPinSolid,
    heroPhoneSolid,
    heroPlayCircleSolid,
    heroStarSolid
} from '@ng-icons/heroicons/solid';

import { ionCarOutline, ionPeopleOutline, ionStar } from '@ng-icons/ionicons';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    heroAdjustmentsVerticalMini,
    heroChevronLeftMini,
    heroChevronRightMini,
    heroChevronUpDownMini
} from '@ng-icons/heroicons/mini';
import { ReactiveFormsModule } from '@angular/forms';
import {
    currentUserFromLocalStorageInitializer
} from './core/initializers/current-user-initializer';
import { CURRENT_ITEM_SERVICE_CONFIG } from './search/services/current-item.service';
import { provideIntercom } from './intercom/intercom.provider';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import LaraPlanmeister from '../theme/LaraPlanmeister';
import { provideApollo } from 'apollo-angular';
import { environment } from '../environments/environment';
import { createClient } from 'graphql-ws';
import { AuthService } from './auth/services/auth.service';
import { HttpLink } from 'apollo-angular/http';
import { ApolloClientOptions, InMemoryCache, split, from } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { NotificationService } from "@planmeister/pithos";
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPiwikProModule, NgxPiwikProRouterModule } from '@piwikpro/ngx-piwik-pro';

registerLocaleData(localeNl);

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) => {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            routes,
            withPreloading(PreloadAllModules)
        ),
        provideHttpClient(
            withJsonpSupport()
        ),
        importProvidersFrom(
            NgxPiwikProModule.forRoot('59b86171-d0bf-4de5-9007-34c7fab25793', 'https://planmeister.containers.piwik.pro'),
            NgxPiwikProRouterModule,
            // GraphQLModule,
            NgIconsModule.withIcons({
                heroUserPlus,
                heroSpeakerWave,
                heroBars2,
                heroUser,
                heroInformationCircle,
                heroArrowsUpDown,
                heroBuildingOffice,
                heroExclamationTriangle,
                heroSquares2x2,
                heroMagnifyingGlass,
                heroMagnifyingGlassSolid,
                ionCarOutline,
                ionPeopleOutline,
                heroChevronRight,
                heroChevronUpDownMini,
                heroMapPinSolid,
                heroCalendarSolid,
                heroEnvelopeSolid,
                heroChevronLeftMini,
                heroChevronRightMini,
                heroXMark,
                heroChevronDoubleLeft,
                heroChevronDoubleUp,
                heroAdjustmentsVerticalMini,
                heroEllipsisVertical,
                heroAdjustmentsVertical,
                heroMapPin,
                heroChevronUp,
                heroChevronDown,
                heroCog,
                heroArrowTrendingUp,
                heroEye,
                heroBars3,
                heroPencil,
                heroEnvelope,
                heroArrowsRightLeft,
                heroPlus,
                heroMinus,
                heroPhone,
                heroPhoneSolid,
                heroCursorArrowRays,
                heroUserCircle,
                heroUsers,
                heroBell,
                heroFingerPrint,
                heroCreditCard,
                heroDevicePhoneMobile,
                heroPlayCircleSolid,
                heroArrowUpRight,
                heroArrowTopRightOnSquare,
                heroGlobeEuropeAfrica,
                heroChevronUpDown,
                heroBuildingOffice2,
                heroPhoto,
                ionStar,
                heroStar,
                heroFunnelSolid,
                heroArrowSmallUp,
                heroArrowSmallDown,
                heroShoppingCart,
                heroStarSolid
            }),
            ReactiveFormsModule,
            BrowserAnimationsModule,
            LeafletModule,
            LeafletMarkerClusterModule
        ),
        provideRouter(routes,
            withRouterConfig({
                paramsInheritanceStrategy: 'always'
            }),
            withComponentInputBinding()
        ),
        {
            provide: CURRENT_ITEM_SERVICE_CONFIG,
            useValue: {
                pollInterval: 30000 // Poll every 30 seconds
            }
        },

        provideAppInitializer(() => {
        const initializerFn = (currentUserFromLocalStorageInitializer)();
        return initializerFn();
      }),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler()
        },
        {
            provide: Sentry.TraceService,
            deps: [ Router ]
        },
        provideAppInitializer(() => {
        const initializerFn = (() => () => {
            })();
        return initializerFn();
      }),
        { provide: LOCALE_ID, useValue: 'nl' },
        { provide: 'googleTagManagerId', useValue: 'GTM-TBMBX2P' },
        provideIntercom({
            appId: 'a1pmql11',
            region: 'us',
            hideLauncher: true
        }),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: LaraPlanmeister,
                options: {
                    darkModeSelector: '.dark',
                }
            },
            translation: {
                dayNames: [ 'zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag' ],
                dayNamesShort: [ 'zo', 'ma', 'di', 'wo', 'do', 'vr', 'za' ],
                dayNamesMin: [ 'zo', 'ma', 'di', 'wo', 'do', 'vr', 'za' ],
                monthNames: [ 'januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december' ],
                monthNamesShort: [ 'jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec' ],
                today: 'Vandaag',
                clear: 'Leegmaken',
                dateFormat: 'dd/mm/yy',
                weekHeader: 'Wk',
                chooseYear: 'Jaar kiezen',
                prevYear: 'Vorig jaar',
                nextYear: 'Volgend jaar',
                prevMonth: 'Vorige maand',
                nextMonth: 'Volgende maand',
                prevDecade: 'Vorig decennium',
                nextDecade: 'Volgend decennium',
                prevSecond: 'Vorige seconde',
                nextSecond: 'Volgende seconde',
                prevHour: 'Vorig uur',
                nextHour: 'Komend uur',
                prevMinute: 'Vorige minuut',
                nextMinute: 'Volgende minuut'
            }
        }),
        provideApollo(() => {
            const httpUri = environment.clientGraphQLApi.httpUrl;
            const wsUri = environment.clientGraphQLApi.wsUrl;
            const notificationService = inject(NotificationService);

            const wsLink = new GraphQLWsLink(createClient({
                url: wsUri,
                connectionParams: () => {
                    const token = localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
                    return token ? { Authorization: `Bearer ${token}` } : {};
                },
            }));

            const errorLink = onError(({ graphQLErrors, networkError }) => {
                if (graphQLErrors) {
                    const message = graphQLErrors[0].message;
                    notificationService.add({
                        type: 'error',
                        message,
                    });
                }
                if (networkError) {
                    console.error(`[Network error]: ${networkError}`);
                    notificationService.add({
                        type: 'error',
                        message: networkError.message,
                    });
                }
            });

            const authLink = setContext((_, { headers }) => {
                const token = localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
                return {
                    headers: {
                        ...headers,
                        authorization: token ? `Bearer ${token}` : "",
                    }
                };
            });

            const httpLinkWithError = from([
                errorLink,
                authLink,
                inject(HttpLink).create({
                    uri: httpUri,
                })
            ]);

            const splitLink = split(
                ({ query }) => {
                    const definition = getMainDefinition(query);
                    return (
                        definition.kind === 'OperationDefinition' &&
                        definition.operation === 'subscription'
                    );
                },
                wsLink,
                httpLinkWithError
            );

            return {
                link: splitLink,
                cache: new InMemoryCache(),
                devtools: {
                    enabled: true,
                },
                defaultOptions: {
                }
            } as ApolloClientOptions<any>;
        }),
        provideTranslateService({
            defaultLanguage: 'nl',
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
        }),
    ]
};
